import { RiDownloadLine } from '@remixicon/react';
import type { ReactNode } from 'react';

import { Button } from '@playbook/atoms/button';

import { downloadPrivateKey } from '@portal/pkg/crypto/keypair';
import { CodeBlock } from '@portal/views/_shared/code-block';
import { Notice } from '@portal/views/_shared/notice';

interface UnsavedPrivateKeyProps {
	/** Names the downloaded file, as it does on the issued screen. */
	name: string;
	privateKey: string;
}

/**
 *	The private half of a pair this browser made for a request that did not
 *	come back with an answer.
 *
 *	A request whose response was lost still reached the server, so the key may
 *	be live — and this browser holds the only copy of its private half, which
 *	nothing can reissue. Offering it beats discarding it on the assumption the
 *	request failed; a key the server never stored makes this a harmless file.
 */
export function UnsavedPrivateKey({
	name,
	privateKey,
}: UnsavedPrivateKeyProps): ReactNode {
	return (
		<Notice variant="warning" title="Save this private key before you leave">
			Your request may have gone through. Check your keys — if one is there,
			this is the only copy of the half that signs for it.
			<CodeBlock
				name="privateKey"
				value={privateKey}
				rows={7}
				action={
					<Button
						variant="primary"
						label="Download .pem"
						leadingIcon={<RiDownloadLine />}
						onClick={() => downloadPrivateKey(name, privateKey)}
					/>
				}
			/>
		</Notice>
	);
}
